/**
 * Data layer for `igaming-active-player-tracking`. Thin typed fetch wrappers over /api/v1/igaming-active-player-tracking.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface IgamingActivePlayerTrackingRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListIgamingActivePlayerTrackingResponse {
	count: number;
	items: IgamingActivePlayerTrackingRow[];
}

export interface CreateIgamingActivePlayerTrackingBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/igaming-active-player-tracking";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListIgamingActivePlayerTrackingResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListIgamingActivePlayerTrackingResponse>(res);
}

export async function create(body: CreateIgamingActivePlayerTrackingBody): Promise<IgamingActivePlayerTrackingRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<IgamingActivePlayerTrackingRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const igamingActivePlayerTrackingQueryKeys = {
	all: ["igaming-active-player-tracking"] as const,
	list: () => [...igamingActivePlayerTrackingQueryKeys.all] as const,
	detail: (id: string) => [...igamingActivePlayerTrackingQueryKeys.all, id] as const,
};
